/**
 * Which downlink effects are switched on (SPEC §3.3.2).
 *
 * All ten effects are individually toggleable for accessibility and performance. Two of them
 * carry gameplay signal, so turning them off is not free: `exposure` off has to surface the gain
 * state on the HUD, and `blocking` off leaves the link-quality bar as the only warning that the
 * picture is being jammed. Both compensations live in the HUD, not here — this module only
 * records the choice.
 *
 * The keys are the effect list of §3.3.2 in order, plus FXAA, which is not an artefact but has
 * to live in the same stack because `antialias: false` is set on the renderer (SPEC §4.5).
 */
export interface PostSettings {
  distortion: boolean;
  chromatic: boolean;
  exposure: boolean;
  grade: boolean;
  haze: boolean;
  blocking: boolean;
  jitter: boolean;
  rollingShutter: boolean;
  grain: boolean;
  vignette: boolean;
  fxaa: boolean;
}

export type PostEffectKey = keyof PostSettings;

/** Effect keys in the order of SPEC §3.3.2, for panels and settings screens. */
export const POST_EFFECT_KEYS: readonly PostEffectKey[] = [
  'distortion',
  'chromatic',
  'exposure',
  'grade',
  'haze',
  'blocking',
  'jitter',
  'rollingShutter',
  'grain',
  'vignette',
  'fxaa',
];

/** Short labels for the debug panel and, at M10, the settings screen. */
export const POST_EFFECT_LABELS: Record<PostEffectKey, string> = {
  distortion: 'BARREL',
  chromatic: 'ABERRATION',
  exposure: 'AUTO-EXPOSURE',
  grade: 'COLOUR GRADE',
  haze: 'HAZE',
  blocking: 'COMPRESSION',
  jitter: 'JITTER / HOLD',
  rollingShutter: 'ROLLING SHUTTER',
  grain: 'SENSOR GRAIN',
  vignette: 'VIGNETTE',
  fxaa: 'FXAA',
};

/** Everything on — the shipping default. */
export function createPostSettings(): PostSettings {
  return {
    distortion: true,
    chromatic: true,
    exposure: true,
    grade: true,
    haze: true,
    blocking: true,
    jitter: true,
    rollingShutter: true,
    grain: true,
    vignette: true,
    fxaa: true,
  };
}

/** True when nothing is enabled, in which case the stack can be bypassed entirely. */
export function anyEffectEnabled(settings: PostSettings): boolean {
  for (let i = 0; i < POST_EFFECT_KEYS.length; i += 1) {
    if (settings[POST_EFFECT_KEYS[i]]) return true;
  }
  return false;
}

/**
 * Apply a `?post=` URL override. `?post=0` (or `off`/`none`) disables everything; otherwise the
 * value is a comma-separated list of the effects to leave on, e.g. `?post=grade,haze,fxaa`.
 * Unknown names are ignored rather than fatal — this is a debug affordance, not a mission file.
 */
export function applyPostOverride(settings: PostSettings, raw: string | null): PostSettings {
  if (raw === null) return settings;

  const value = raw.trim().toLowerCase();
  for (let i = 0; i < POST_EFFECT_KEYS.length; i += 1) settings[POST_EFFECT_KEYS[i]] = false;
  if (value === '0' || value === 'off' || value === 'none' || value === '') return settings;
  if (value === '1' || value === 'all') {
    for (let i = 0; i < POST_EFFECT_KEYS.length; i += 1) settings[POST_EFFECT_KEYS[i]] = true;
    return settings;
  }

  for (const name of value.split(',')) {
    const key = name.trim() as PostEffectKey;
    if (Object.prototype.hasOwnProperty.call(settings, key)) settings[key] = true;
  }
  return settings;
}
